import { ClipboardCheck, Clock } from "lucide-react";

/**
 * One-time Myo Check-In acknowledgment popup, shown the first time a parent opens
 * a session whose Check-In hasn't been submitted yet. NOT dismissible by backdrop
 * click or Escape — the parent must actively tap "Fill it out now" or "I'll do it
 * later". Whichever they pick, the parent's choice is timestamp-logged on the
 * server for refund/dispute purposes. The persistent reminder banner keeps nudging
 * them after this popup, so this is purely a "you were notified" audit record.
 */
export default function CheckInReminderModal({ open, sessionNumber, tribe, onFillNow, onLater, submitting }) {
  if (!open) return null;
  // Neutral tribe fallback so this modal works even when the tribe object is missing
  // (self-enrolled adults, "No tribe" pathway).
  const accent = tribe?.accent || "#8B5E7A";
  const ink = tribe?.ink || "#1E1E2E";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="checkin-reminder-title"
      data-testid="checkin-reminder-modal"
    >
      {/* Backdrop is intentionally NOT click-dismissible — the parent must tap one of
          the two buttons so their choice is logged. */}
      <div className="absolute inset-0 bg-navy/60 backdrop-blur-sm" />
      <div
        className="relative max-w-md w-full bg-warm-white rounded-3xl shadow-[0_30px_80px_rgba(44,45,91,0.35)] p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center"
          style={{ background: accent }}
        >
          <ClipboardCheck size={22} className="text-warm-white" />
        </div>

        <div className="text-[10px] uppercase tracking-[0.3em] font-bold mt-4" style={{ color: accent }}>
          Session {sessionNumber} · Myo Check-In
        </div>
        <h2
          id="checkin-reminder-title"
          className="font-display text-2xl font-bold mt-1"
          style={{ color: ink }}
          data-testid="checkin-reminder-title"
        >
          Don't skip your check-in.
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-ink-dark" data-testid="checkin-reminder-body">
          Remember, we need to fill out your Myo Check-In at the beginning of each session — this helps
          us track progress and is required for your program guarantee.
        </p>

        <div className="mt-6 grid gap-2">
          <button
            type="button"
            disabled={submitting}
            onClick={onFillNow}
            data-testid="checkin-reminder-fill-now"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold disabled:opacity-50"
            style={{ background: accent, color: "#FFFEF9" }}
          >
            <ClipboardCheck size={16} /> {submitting ? "Saving…" : "Fill it out now"}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={onLater}
            data-testid="checkin-reminder-later"
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold border-2 disabled:opacity-50"
            style={{ borderColor: accent, color: ink, background: "transparent" }}
          >
            <Clock size={14} /> I'll do it later
          </button>
        </div>

        <p className="mt-4 text-[11px] leading-relaxed text-ink-soft" data-testid="checkin-reminder-footnote">
          Either choice is recorded on your account so we have a clear log that we notified you.
        </p>
      </div>
    </div>
  );
}
